import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/store/userStore';
import { useFeatureStore } from '@/store/featureStore';

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/HomeView.vue'),
    meta: { hideMenu: false, hideNav: true, title: 'Itac' },
  },
  {
    path: '/argos',
    name: 'argos',
    component: () => import('../views/UploadView.vue'),
    meta: { hideMenu: false, title: 'Argos' },
  },
  {
    path: '/ulis/:peopleId?',
    name: 'ulis',
    component: () => import('../views/ListView.vue'),
    meta: { hideMenu: false, title: 'Ulis' },
  },
  {
    path: '/alcinos',
    name: 'alcinos',
    component: () => import('../views/EventView.vue'),
    meta: { hideMenu: false, title: 'Alcinos', requiredFlag: 'alcinos' },
  },
  {
    path: '/admin',
    name: 'admin',
    component: () => import('../views/AdminView.vue'),
    meta: { hideMenu: false, title: 'Itac', requiredRole: 'Admin' },
  },
  {
    path: '/admin/migration-auraface',
    name: 'migration-auraface',
    component: () => import('../views/MigrationView.vue'),
    meta: { hideMenu: false, title: 'Itac', requiredRole: 'Admin' },
  },
  {
    path: '/rms',
    name: 'RMS',
    component: () => import('../views/RmsView.vue'),
    meta: { hideMenu: false, title: 'Itac', requiredFlag: 'rms' },
  },
  {
    path: '/maintenance',
    name: 'maintenance',
    component: () => import('../views/MaintenanceView.vue'),
    meta: { hideMenu: true, title: 'Maintenance' },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'home' },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to, from, next) => {
  const userStore = useUserStore();

  if (to.name === 'maintenance') {
    return next();
  }

  // Vérifier si le mode maintenance est activé. Si il l'est, il faut vérifier le rôle de l'utilisateur et lui couper l'accès si il n'est pas admin ou weaverize.
  if (userStore.maintenanceMode) {
    if (!userStore.isAdmin) {
      return next({ name: 'maintenance' });
    }
  }

  // Vérifier si la route est protégée par un feature flag
  if (to.meta.requiredFlag) {
    const featureStore = useFeatureStore();
    if (!featureStore.isEnabled(to.meta.requiredFlag)) {
      return next({ name: 'home' });
    }
  }

  // Vérifier si la route nécessite un rôle spécifique
  if (to.meta.requiredRole) {
    const hasRequiredRole = to.meta.requiredRole === 'Admin'
      ? userStore.isAdmin
      : userStore.hasRole(to.meta.requiredRole);
    if (hasRequiredRole) {
      return next();
    }

    // L'utilisateur n'a pas le rôle requis, rediriger vers home
    console.warn('Access denied to', to.path, ': required role', to.meta.requiredRole, 'not found');
    return next({ name: 'home' });
  }

  next();
});

// Pendant un rolling update K8s, un pod ancien peut retourner 404 pour un chunk
// généré par un pod récent. On force un rechargement complet pour récupérer le
// bon index.html et les bons chunks. Une seule tentative par session pour éviter
// les boucles infinies si le CDN a mis la 404 en cache.
router.onError((error, to) => {
  const isChunkError = error.name === 'ChunkLoadError' || /Loading chunk/.test(error.message ?? '');
  if (!isChunkError) return;
  if (sessionStorage.getItem('_chunk_load_retried')) return;
  sessionStorage.setItem('_chunk_load_retried', '1');
  window.location.replace(to.fullPath);
});

router.afterEach(() => sessionStorage.removeItem('_chunk_load_retried'));

export default router;
